import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchCollegeCornerDataCommonAPI } from '../../../../actions/college-corner.actions';
import BackgroundImage from '../../../../components/BackgroundImage';
import { getRatingDetails, setRatingChoice } from '../../../../utils';
import {
    getDefaultAvatar,
    getNameInitials,
} from '../../../../utils/collegeCorner';

import styles from './CollegeCornerOverview.scss';

function CollegeCornerOverviewData({
    title,
    api,
    type,
    viewAll,
    isLoggedIn = false,
    userIsProCollegeClubOwnerValue = false,
    handleLearnMoreUtrCollegeState,
    hideMaskedP6,
    isRostersRolloutEnabled = false,
}) {
    const dispatch = useDispatch();
    const [data, setData] = useState([]);

    const loadData = async () => {
        const response = await dispatch(fetchCollegeCornerDataCommonAPI(api));
        if (response) {
            setData(response);
        }
    };

    useEffect(() => {
        if (api) {
            loadData();
        }
    }, [api]);

    const getRowData = (rows) => {
        let rowsData = [];
        switch (type) {
            case 'profiles':
                rowsData = rows
                    .map(({ id = '', source }) => {
                        const {
                            displayName = '',
                            gender = '',
                            playerProfileImages = {},
                            location,
                            firstName = '',
                            lastName = '',
                        } = source || {};
                        const singlesRating = getRatingDetails(
                            setRatingChoice({
                                player: source,
                                ratingType: 'singles',
                                utrType: 'sanctioned',
                            }),
                            false,
                            false,
                            false,
                            false,
                        );
                        const { verified, value } = singlesRating;
                        const maskedUtrRating = parseInt(value) ? `${parseInt(value)}.xx` : value;
                        return {
                            id,
                            displayName,
                            baseURL: '/v1/player/',
                            gender,
                            rating: hideMaskedP6 ? `UTR: ${value}` : `UTR: ${maskedUtrRating}`,
                            isVerified: verified,
                            location: location?.display,
                            image: playerProfileImages,
                            defaultImage: '/img/v2/icons/icon-avatar-player.svg',
                            firstName,
                            lastName,
                        };
                    });
                break;
            case 'colleges':
                rowsData = rows.map(({ id = '', source }) => {
                    const {
                        activeRoster,
                        clubProfileImages = {},
                        school,
                        location,
                        sanctioned = false,
                    } = source || {};

                    const power6 = isRostersRolloutEnabled
                        ? (activeRoster?.power6?.power6Rating ?? '')
                        : (school?.power6 ?? '');

                    const maskedP6Rating = power6 && typeof power6 === 'number'
                        ? `${Math.trunc(power6)}.xx`
                        : '';
                    const unmaskedP6Rating = power6 && typeof power6 === 'number'
                        ? power6.toFixed(2)
                        : '';
                    const p6Display = hideMaskedP6 ? unmaskedP6Rating : maskedP6Rating;

                    return {
                        id,
                        baseURL: '/v1/club/',
                        displayName: school?.displayName,
                        rating: p6Display ? `P6: ${p6Display}` : '',
                        isVerified: sanctioned,
                        location: location?.display,
                        image: clubProfileImages,
                        defaultImage: getDefaultAvatar(source),
                    };
                });
                break;
            default:
                rowsData = [];
                break;
        }
        return rowsData;
    };

    const renderConditionalLink = () => {
        return isLoggedIn ? (
            <Link
                to={`/college-corner/${viewAll}`}
                className={`link`}
                data-testid={`college-corner.overview.${viewAll}.view-all-link`}
            >
                View all
            </Link>
        ) : (
            <a onClick={() => handleLearnMoreUtrCollegeState(viewAll)} className={`link`} data-testid={`college-corner.overview.${viewAll}.view-all-btn`}>
                View all
            </a>
        );
    };

    return (
        <div className={`${styles.collegeCornerOverviewData}`}>
            <h4 data-testid={`college-corner.overview.${title}.heading`}>{title}</h4>
            <ul className={`${styles.box}`}>
                {data.length ? (
                    getRowData(data).map(
                        (
                            {
                                displayName,
                                isVerified,
                                id,
                                rating,
                                location,
                                image,
                                defaultImage,
                                gender,
                                baseURL,
                                firstName,
                                lastName,
                            },
                            index,
                        ) => (
                            <li key={index + 1} data-testid={`college-corner.overview.${type}-${id}.row`}>
                                <div data-testid={`college-corner.overview.${type}-${id}.rank`}>{index + 1}</div>
                                <div>
                                    <div className={`${styles.displayLogo}`} data-testid={`college-corner.overview.${type}-${id}.logo`}>
                                        {((type === 'colleges' && image) ||
                                            (type === 'profiles' && image?.default)) && (
                                                <BackgroundImage
                                                    baseUrl={baseURL}
                                                    defaultImage={defaultImage}
                                                    images={image}
                                                    imageType={'icon'}
                                                    parentStylesClass={styles.profilePicture}
                                                />
                                            )}
                                        {type === 'profiles' && image && !image.default && (
                                            <div className="avatar-placeholder border-grad-rad" data-testid={`college-corner.overview.${type}-${id}.avatar`}>
                                                {getNameInitials({ firstName, lastName })}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        {displayName && (
                                            <Link
                                                to={`/${type}/${id}`}
                                                className={`${styles.displayTitle}`}
                                                data-testid={`college-corner.overview.${type}-${id}.profile-link`}
                                            >
                                                {displayName}
                                            </Link>
                                        )}
                                        <div className={`${styles.displayExtra}`} data-testid={`college-corner.overview.${type}-${id}.extra`}>
                                            <div>
                                                {rating && <span data-testid={`college-corner.overview.${type}-${id}.rating`}>{rating}</span>}
                                                {type === 'profiles' && isVerified && (
                                                    <span className={`${styles.verified} verified`} />
                                                )}
                                            </div>
                                            {gender && <div data-testid={`college-corner.overview.${type}-${id}.gender`}>{gender}</div>}
                                            {location && <div data-testid={`college-corner.overview.${type}-${id}.location`}>{location}</div>}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ),
                    )
                ) : (
                    <li className={`${styles.loadingData}`} data-testid="college-corner.overview.loading">
                        <span data-testid="college-corner.overview.loading.text">
                            <i className="fas fa-circle-notch fa-spin" /> Loading Data...
                        </span>
                    </li>
                )}
            </ul>
            <div className={`${styles.boxFoot}`}>{renderConditionalLink()}</div>
        </div>
    );
}

CollegeCornerOverviewData.propTypes = {
    title: PropTypes.string,
    api: PropTypes.string,
    type: PropTypes.string,
    viewAll: PropTypes.string,
    hideMaskedP6: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    userIsProCollegeClubOwnerValue: PropTypes.bool,
    handleLearnMoreUtrCollegeState: PropTypes.func,
    isRostersRolloutEnabled: PropTypes.bool,
};

CollegeCornerOverviewData.defaultProps = {
    title: '',
    api: '',
    type: '',
    viewAll: '',
    hideMaskedP6: false,
    isLoggedIn: false,
    userIsProCollegeClubOwnerValue: false,
    handleLearnMoreUtrCollegeState: () => { },
    isRostersRolloutEnabled: false,
};

export default CollegeCornerOverviewData;
